/**
 * cultureAmpParser — parses raw Culture Amp paste text into structured Q&A blocks.
 *
 * Used by FeedbackSidebar to display structured reviewer cards when the
 * manager opens the multi-source feedback panel during the assessment.
 */

// ── Constants ───────────────────────────────────────────────────────────────

const ICONS = new Set([
  'keyboard_arrow_down', 'keyboard_arrow_up', 'keyboard_tab',
  'arrow_forward', 'arrow_back', 'cloud_download', 'settings',
  'forum', 'close', 'check', 'more_vert', 'search',
]);

const NAV_EXACT = new Set([
  'Skip to content', 'Culture Amp Home', 'Home', 'Feedback',
  '1-on-1s', 'Goals', 'Performance', 'Analytics', 'Coach',
  'Settings', 'Help & chat', 'Back to overview page', 'Export',
  'current section', 'Self-reflections', 'External feedback',
  'View Self-reflection - Culture Amp', 'View feedback - Culture Amp',
  'Complete', 'Self-reflection activity', 'Peer feedback',
  'Upward feedback', 'Anytime feedback', 'Feedback received',
]);

const MONTHS = 'Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec';
const DATE_RE = new RegExp(
  `^(?:(?:${MONTHS})[a-z]*\\.?\\s+\\d{1,2}|\\d{1,2}\\s+(?:${MONTHS}))`,
  'i',
);

// Sub-instruction starters (lighter display in sidebar)
const SUB_RE = /^(?:Describe|Refer to|You can|Please|Consider|Think about|Include|Note:|For example)/i;

// Standalone question number on its own line (e.g. "1." or "3")
const Q_NUM_RE = /^\d+\.?\s*$/;

// Name heuristic: Title-case words (supports German umlauts), 3–60 chars
const NAME_RE = /^[A-ZÄÖÜ][a-zäöüßé]+([\s\-'][A-ZÄÖÜ][a-zäöüßé]+)+$/;
const INITIALS_RE = /^[A-Z]{2}$/;

// ── Types ───────────────────────────────────────────────────────────────────

export interface ParsedQA {
  question: string;
  subInstruction?: string;
  answer: string;
}

export interface ParsedReviewerBlock {
  reviewer: string;
  date: string;
  type: 'Peer' | 'Upward' | 'Self';
  qas: ParsedQA[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Strip nav chrome and icon text from a raw paste, returning trimmed lines. */
function cleanLines(raw: string): string[] {
  return raw.split('\n').map(l => l.trim()).filter(l => {
    if (!l) return true; // preserve blank lines as separators
    if (ICONS.has(l) || NAV_EXACT.has(l)) return false;
    if (l.startsWith('Performance cycles/')) return false;
    if (/^Performance Review Cycle /.test(l)) return false;
    return true;
  });
}

function isDateLine(line: string): boolean {
  return DATE_RE.test(line);
}

function isNameLine(line: string): boolean {
  if (!line || line.length > 60 || line.length < 3) return false;
  if (line.endsWith('?') || line.endsWith('.')) return false;
  if (SUB_RE.test(line)) return false;
  if (INITIALS_RE.test(line)) return true;
  if (NAME_RE.test(line)) return true;
  return false;
}

/** Parse a list of cleaned body lines into Q&A objects. */
function parseQAsFromLines(bodyLines: string[]): ParsedQA[] {
  const qas: ParsedQA[] = [];
  let i = 0;

  while (i < bodyLines.length) {
    if (!Q_NUM_RE.test(bodyLines[i])) { i++; continue; }

    i++; // consume question number
    while (i < bodyLines.length && !bodyLines[i]) i++; // skip blanks
    if (i >= bodyLines.length) break;

    const question = bodyLines[i];
    i++;

    while (i < bodyLines.length && !bodyLines[i]) i++; // skip blanks

    let subInstruction: string | undefined;
    if (i < bodyLines.length && SUB_RE.test(bodyLines[i])) {
      subInstruction = bodyLines[i];
      i++;
    }

    // Collect answer lines until the next question number
    const answerParts: string[] = [];
    while (i < bodyLines.length && !Q_NUM_RE.test(bodyLines[i])) {
      answerParts.push(bodyLines[i]);
      i++;
    }

    const answer = answerParts.join('\n').trim();
    if (question) qas.push({ question, subInstruction, answer });
  }

  return qas;
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Parse a raw multi-reviewer paste (performance review or anytime feedback).
 * Uses date lines as anchors — each block starts just before a date line and
 * ends just before the next one. Reviewer names are detected by looking back
 * from the date line.
 */
export function parseMultiReviewerFeedback(
  raw: string,
  type: 'Peer' | 'Upward' = 'Peer',
): ParsedReviewerBlock[] {
  if (!raw.trim()) return [];

  const lines = cleanLines(raw);
  const blocks: ParsedReviewerBlock[] = [];

  // Locate all date lines
  const dateIndices: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] && isDateLine(lines[i])) dateIndices.push(i);
  }

  if (dateIndices.length === 0) {
    // No date found — treat whole content as one anonymous block
    const qas = parseQAsFromLines(lines);
    if (qas.length > 0) blocks.push({ reviewer: '', date: '', type, qas });
    return blocks;
  }

  for (let di = 0; di < dateIndices.length; di++) {
    const dateIdx = dateIndices[di];
    const nextDateIdx = di + 1 < dateIndices.length ? dateIndices[di + 1] : lines.length;

    // Walk backwards from date to find reviewer name
    let reviewer = '';
    let lookBack = dateIdx - 1;
    while (lookBack >= 0 && !lines[lookBack]) lookBack--;

    if (lookBack >= 0 && isNameLine(lines[lookBack])) {
      if (INITIALS_RE.test(lines[lookBack])) {
        // Two-letter initials — real name is one step further back
        lookBack--;
        while (lookBack >= 0 && !lines[lookBack]) lookBack--;
        if (lookBack >= 0 && isNameLine(lines[lookBack])) {
          reviewer = lines[lookBack];
        }
      } else {
        // Could be duplicate name (Culture Amp repeats reviewer name twice)
        const oneMore = lookBack - 1;
        reviewer =
          oneMore >= 0 && lines[oneMore] === lines[lookBack]
            ? lines[lookBack]
            : lines[lookBack];
      }
    }

    const date = lines[dateIdx];
    const bodyLines = lines.slice(dateIdx + 1, nextDateIdx);
    const qas = parseQAsFromLines(bodyLines);

    blocks.push({ reviewer, date, type, qas });
  }

  return blocks;
}

/**
 * Parse a raw self-review paste into a single structured block.
 * The reviewer name (employee) and date are detected automatically.
 */
export function parseSelfReview(raw: string): ParsedReviewerBlock | null {
  if (!raw.trim()) return null;

  const lines = cleanLines(raw);

  let date = '';
  let dateIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] && isDateLine(lines[i])) {
      date = lines[i];
      dateIdx = i;
      break;
    }
  }

  // Reviewer name: last name-like line before the date
  let reviewer = '';
  if (dateIdx > 0) {
    let lookBack = dateIdx - 1;
    while (lookBack >= 0 && !lines[lookBack]) lookBack--;
    if (lookBack >= 0 && isNameLine(lines[lookBack])) reviewer = lines[lookBack];
  }

  const bodyStart = dateIdx >= 0 ? dateIdx + 1 : 0;
  const qas = parseQAsFromLines(lines.slice(bodyStart));

  return { reviewer, date, type: 'Self', qas };
}

/**
 * Simple boilerplate cleaner for textarea display (not structured parsing).
 * Returns a clean string with icon/nav lines stripped and runs of blanks collapsed.
 */
export function cleanCultureAmpPaste(raw: string): string {
  return cleanLines(raw).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
